import { anchorLabel, parseDay, sendDate, type KeyDateRef, type Touch } from "./emailSmsTypes";
import { tokensIn } from "./touchCopy";

/*
    Tokens that come from the client's own brand or sending account rather than from the
    student. These are the ones that block a send: an empty [first_name] is one odd-looking
    greeting, but an empty [support_phone] is a message telling somebody to call nowhere.
    Per-student tokens are never blocking.
*/
export const BRAND_TOKEN_LABELS: Record<string, string> = {
  support_phone: "Support phone",
  school_name: "School name",
  school_short: "Short name",
};

export type TouchBlock = {
  head: string;
  rest: string;
  /** Label of the link that takes the operator to where the fix lives. */
  fix: string;
  target: "key-dates" | "accounts" | "branding" | "campaign-dates";
};

const shortDate = (date: Date) =>
  date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

const longDate = (date: Date) =>
  date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

export type BlockContext = {
  keyDates: KeyDateRef[];
  /** Every key date type's display name, so a warning can name one the campaign has not set. */
  typeLabels?: Record<string, string>;
  values: Record<string, string>;
  campaignStart?: string | null;
  campaignEnd?: string | null;
  /** Once a campaign is serving, past dates are history rather than a mistake. */
  serving?: boolean;
  today?: Date;
};

/*
    Why a touch will not send, or null. Order matters and is not arbitrary: each rule is a
    precondition for the next being meaningful. Without the key date there is no send date to
    compare, and a date outside the campaign window is not worth reporting on a touch whose
    copy cannot render anyway.
*/
export function blockFor(touch: Touch, ctx: BlockContext): TouchBlock | null {
  const anchor = ctx.keyDates.find((k) => k.type === touch.anchor);
  if (!anchor?.date) {
    return {
      head: `${anchorLabel(touch.anchor, ctx.keyDates, ctx.typeLabels)} isn't on this campaign.`,
      rest: "This touch has no send date until it's added to Key dates.",
      fix: "Add it in Key dates",
      target: "key-dates",
    };
  }

  const copy =
    touch.ch === "email"
      ? [touch.subject, touch.pre, touch.body].filter(Boolean).join(" ")
      : (touch.text ?? "");
  const missing = tokensIn(copy).filter(
    (key) => BRAND_TOKEN_LABELS[key] && !ctx.values[key],
  );
  if (missing.length) {
    const first = missing[0];
    return {
      head: `${BRAND_TOKEN_LABELS[first]} is empty.`,
      rest: "This touch merges it, so it can't send until it's set.",
      fix: first === "support_phone" ? "Manage sending accounts" : "Open Branding",
      target: first === "support_phone" ? "accounts" : "branding",
    };
  }

  const when = sendDate(touch, ctx.keyDates);
  if (!when) return null;

  const today = ctx.today ?? new Date();
  today.setHours(0, 0, 0, 0);

  if (!ctx.serving && when < today) {
    return {
      head: "Send date already passed.",
      rest: `${longDate(when)} is before today, so this touch won't send.`,
      fix: "Edit Campaign Dates",
      target: "campaign-dates",
    };
  }

  const start = parseDay(ctx.campaignStart);
  if (start && !isNaN(start.getTime()) && when < start) {
    return {
      head: "Sends before the campaign starts.",
      rest: `${shortDate(when)} is before the ${shortDate(start)} start, so this touch won't send.`,
      fix: "Edit Campaign Dates",
      target: "campaign-dates",
    };
  }

  const end = parseDay(ctx.campaignEnd);
  if (end && !isNaN(end.getTime()) && when > end) {
    return {
      head: "Sends after the campaign ends.",
      rest: `${shortDate(when)} is after the ${shortDate(end)} end, so this touch won't send.`,
      fix: "Edit Campaign Dates",
      target: "campaign-dates",
    };
  }

  return null;
}

/*
    The drip doc's rule: no same-day email and text carrying the same ask. Flagged on the
    text, because the email is the one that carries the detail and the text is the one worth
    moving. A warning, not a block — it does not stop approval.
*/
export function conflictsFor(
  touches: Touch[],
  keyDates: KeyDateRef[],
): Map<string, string> {
  const byAsk = new Map<string, { touch: Touch; when: Date }[]>();
  touches.forEach((touch) => {
    const when = sendDate(touch, keyDates);
    if (!when || !touch.cta) return;
    const key = `${when.toDateString()}|${touch.cta}`;
    byAsk.set(key, [...(byAsk.get(key) ?? []), { touch, when }]);
  });

  const out = new Map<string, string>();
  byAsk.forEach((group) => {
    const email = group.find((g) => g.touch.ch === "email");
    if (!email) return;
    group
      .filter((g) => g.touch.ch === "sms")
      .forEach((g) => {
        out.set(
          g.touch.id,
          `${email.touch.title} (email) also sends ${shortDate(g.when)} with "${g.touch.cta}".`,
        );
      });
  });
  return out;
}
